import { ComponentBase } from "../core/ComponentBase.js";
import { DataForm } from "./DataForm.js";

export class DataView extends ComponentBase {

    static styles = `
        .data-view {
            display: flex;
            flex-direction: column;
            height: 100%;
            background-color: var(--bs-body-bg);
            border-radius: var(--ab-radius-md);
        }

        .data-view-full-height {
            height: 100%;
        }

        .data-view-header {
            padding: 1.25rem 1.5rem;
            background-color: var(--bs-body-bg);
            border-bottom: 1px solid var(--bs-border-color);
            border-top-left-radius: var(--ab-radius-md);
            border-top-right-radius: var(--ab-radius-md);
        }

        .data-view-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
        }

        .data-view-toolbar .left-controls {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .data-view-toolbar .right-controls {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }

        .data-view-toolbar .btn-group {
            display: inline-flex;
            align-items: center;
            background-color: var(--bs-secondary-bg);
            padding: 2px;
            border-radius: var(--ab-radius-sm);
            border: 1px solid var(--bs-border-color);
        }

        .data-view-toolbar .btn {
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            padding: var(--ab-btn-pad);
            font-size: var(--ab-text-sm);
            font-weight: var(--ab-weight-medium);
            border-radius: var(--ab-radius-sm);
            border: none;
            transition: transform var(--ab-t-fast), background-color var(--ab-t-fast), box-shadow var(--ab-t-fast);
        }
        .data-view-toolbar .btn:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }

        .data-view-toolbar .btn-outline-secondary {
            background-color: transparent;
            color: var(--bs-secondary-color);
        }
        @media (hover: hover) {
            .data-view-toolbar .btn-outline-secondary:hover {
                background-color: var(--ab-hover-bg);
                color: var(--bs-body-color);
            }
        }

        .data-view-toolbar .btn-primary {
            box-shadow: 0 2px 4px rgba(var(--bs-primary-rgb), 0.15);
        }
        @media (hover: hover) {
            .data-view-toolbar .btn-primary:hover {
                box-shadow: 0 4px 8px rgba(var(--bs-primary-rgb), 0.25);
                transform: var(--ab-lift-sm);
            }
        }

        .data-view-toolbar .view-mode-buttons .btn {
            padding: 0.4rem 0.6rem;
            border-radius: var(--ab-radius-sm);
        }

        .data-view-toolbar .view-mode-buttons .btn.active {
            background-color: var(--bs-body-bg);
            color: var(--bs-primary);
            box-shadow: var(--ab-shadow-xs);
        }

        .data-view-body {
            flex: 1;
            overflow: auto;
            width: 100%;
            scrollbar-width: thin;
            padding: 1.25rem 1.5rem;
        }

        .data-view-filter {
            margin-bottom: 1rem;
        }

        .filter-button {
            position: relative;
        }

        .filter-button.active {
            background-color: var(--bs-primary-bg-subtle);
            border-color: var(--bs-primary-border-subtle);
            color: var(--bs-primary-text-emphasis);
        }

        .filter-button.active::after {
            content: '';
            position: absolute;
            top: 2px;
            right: 2px;
            width: 8px;
            height: 8px;
            background-color: var(--bs-danger);
            border-radius: 50%;
            box-shadow: 0 0 0 2px var(--bs-body-bg);
        }

        /* --- SKELETON SHIMMER LOADER --- */
        .skeleton-loader {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            padding: 0.5rem 0;
            width: 100%;
        }

        .skeleton-row {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.75rem 0;
            border-bottom: 1px solid var(--bs-border-color-subtle);
        }

        .skeleton-block {
            background: linear-gradient(90deg,
                var(--bs-secondary-bg) 25%,
                var(--bs-tertiary-bg) 50%,
                var(--bs-secondary-bg) 75%);
            background-size: 200% 100%;
            animation: ab-shimmer 1.5s infinite linear;
            border-radius: var(--ab-radius-xs);
            height: 1.25rem;
        }

        @keyframes ab-shimmer {
            0% {
                background-position: 200% 0;
            }
            100% {
                background-position: -200% 0;
            }
        }

        /* --- TABLE / GRID LAYOUT STYLES --- */
        .table-responsive {
            border: 1px solid var(--bs-border-color);
            border-radius: var(--ab-radius-sm);
            overflow-x: auto;
            background-color: var(--bs-body-bg);
        }

        .table-hover tbody tr {
            transition: background-color var(--ab-t-base), transform var(--ab-t-base);
        }

        .table-hover tbody tr:hover {
            background-color: var(--ab-hover-bg) !important;
        }

        .table-hover tbody tr.selected {
            background-color: var(--ab-active-bg) !important;
            color: var(--ab-active-color) !important;
        }

        /* --- ACTION CHIP STYLES --- */
        .btn-icon-chip {
            padding: 0.25rem 0.5rem !important;
            font-size: var(--ab-text-2xs) !important;
            border-radius: var(--ab-radius-pill) !important;
            border: 1px solid var(--bs-border-color) !important;
            background-color: var(--bs-body-bg) !important;
            color: var(--bs-secondary-color) !important;
            transition: all var(--ab-t-fast) !important;
        }
        .btn-icon-chip:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring) !important;
        }

        @media (hover: hover) {
            .btn-icon-chip:hover {
                border-color: var(--bs-secondary-color) !important;
                background-color: var(--ab-hover-bg) !important;
                color: var(--bs-body-color) !important;
                transform: scale(1.05);
            }

            .btn-icon-chip.btn-outline-primary:hover {
                border-color: var(--bs-primary) !important;
                color: var(--bs-primary) !important;
                background-color: var(--ab-hover-primary-bg) !important;
            }

            .btn-icon-chip.btn-outline-danger:hover {
                border-color: var(--bs-danger) !important;
                color: var(--bs-danger) !important;
                background-color: var(--bs-danger-bg-subtle, rgba(220, 53, 69, 0.1)) !important;
            }

            .btn-icon-chip.btn-outline-warning:hover {
                border-color: var(--bs-warning) !important;
                color: var(--bs-warning-text-emphasis) !important;
                background-color: rgba(255, 193, 7, 0.1) !important;
            }
        }

        /* --- CARD VIEW MODIFICATIONS --- */
        .data-view .card {
            border: 1px solid var(--bs-border-color);
            border-radius: var(--ab-radius-md);
            transition: transform var(--ab-t-slow),
                        box-shadow var(--ab-t-slow),
                        border-color var(--ab-t-slow);
            box-shadow: var(--ab-shadow-sm);
            background-color: var(--bs-body-bg);
            height: 100%;
        }

        .data-view .card:hover {
            transform: var(--ab-lift-md);
            box-shadow: var(--ab-shadow-md);
            border-color: var(--bs-border-color-translucent);
        }

        .data-view .card.selected {
            border-color: var(--bs-primary) !important;
            background-color: var(--ab-active-bg) !important;
            box-shadow: var(--ab-focus-ring) !important;
        }

        /* --- SEARCH INPUT STYLING --- */
        .toolbar-search {
            position: relative;
            max-width: 250px;
            width: 100%;
        }

        .toolbar-search input {
            padding-left: 2.25rem;
            border-radius: var(--ab-radius-sm);
            border: 1px solid var(--bs-border-color);
            background-color: var(--bs-body-bg);
            font-size: var(--ab-text-sm);
            height: var(--ab-ctrl);
            transition: border-color var(--ab-t-base), box-shadow var(--ab-t-base);
        }

        .toolbar-search input:focus-visible {
            border-color: var(--ab-focus-border);
            box-shadow: var(--ab-focus-ring);
            outline: none;
        }

        .toolbar-search i {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--bs-secondary-color);
            pointer-events: none;
            font-size: var(--ab-text-base);
        }

        @media (max-width: 768px) {
            .data-view-toolbar {
                flex-direction: column;
                align-items: stretch;
                gap: 0.75rem;
            }

            .data-view-toolbar .left-controls {
                flex-direction: column;
                align-items: stretch;
                gap: 0.75rem;
                width: 100%;
            }

            .data-view-toolbar .left-controls .btn-group {
                width: 100%;
            }

            .data-view-toolbar .left-controls .btn-group > .btn {
                flex: 1;
                justify-content: center;
            }

            .data-view-toolbar .left-controls .btn-group > .btn-group {
                flex: 1.5;
            }

            .toolbar-search {
                max-width: 100%;
                width: 100%;
            }

            .data-view-toolbar .right-controls {
                width: 100%;
                justify-content: flex-start;
                margin-top: 0;
            }

            .data-view-toolbar .right-controls > div {
                width: 100%;
            }

            .data-view-toolbar .right-controls .btn {
                flex: 1;
                justify-content: center;
            }
        }
    `;
    constructor(config) {
        // Define template methods before calling super
        const defaultTemplates = {
            gridTemplate: (data) => this.gridTemplate(data),
            listTemplate: (data) => this.listTemplate(data),
            cardTemplate: (data) => this.cardTemplate(data),
            itemTemplate: (data) => this.defaultItemTemplate(data),
            actionButtonTemplate: (action, item) => this.actionButtonTemplate(action, item),
            emptyStateTemplate: () => this.emptyStateTemplate(),
            loadingTemplate: () => this.loadingTemplate(),
            errorTemplate: (error) => this.errorTemplate(error)
        };

        // Merge provided templates with defaults
        const mergedConfig = {
            ...config,
            templates: {
                ...defaultTemplates,
                ...(config.templates || {})
            }
        };

        super(mergedConfig);

        this.data = [];
        this.totalCount = 0;
        this.currentPage = 1;
        this.loadingPage = null;
        this.scrollPos = 0;
        this.loading = false;
        this.error = null;

        this.filterData = null;
        this.filterForm = null;
        this.boundActionHandler = null; // To hold the bound event handler function

    }

    static getDefaultConfig() {
        return {
            idField: "_id",
            fields: [],
            actions: [],
            pageSize: 25,
            autoLoad: true,
            multiSelect: false,
            viewMode: "grid",
            fullHeight: true,
            emptyStateConfig: {
                icon: 'bi bi-inbox',
                message: 'No items found',
                description: 'Try adjusting your search or filters',
                actionButton: null
            },
            containerClass: 'card',
            headerClass: 'card-header border-bottom-0',
            bodyClass: 'card-body',
            footerClass: 'card-footer',
            parentElementId: null,
            filter: {
                enabled: false,
                position: 'top', // 'top', 'left', 'right'
                schema: null,    // Zod schema for filter
                fields: {},      // Filter field configurations
                sections: null,  // Optional sections for filter form
                collapsible: true,
                collapsed: false,
                containerClass: 'card border-0 mb-3'
            },

            toolbar: {
                enabled: true,
                create: {
                    enabled: false,
                    label: 'New',
                    icon: 'bi bi-plus-lg'
                },
                refresh: {
                    enabled: true,
                    label: 'Refresh',
                    icon: 'bi bi-arrow-clockwise'
                },
                viewModes: {
                    enabled: true,
                }
            },

            // Add reference resolution config
            resolveReferences: true,
        };
    }

    async initialize() {
        this.dataProvider = this.config.dataProvider;

        // Create main container with Bootstrap card structure
        this.dataViewContainer = document.createElement("div");
        this.dataViewContainer.setAttribute("id", `${this.eventGroup}-data-view-container`);
        this.dataViewContainer.classList.add('data-view');
        if (this.config?.containerClass?.length > 0) {
            this.dataViewContainer.classList.add(...this.config.containerClass.split(' '));
        }
        if (this.config.fullHeight) {
            this.dataViewContainer.classList.add('data-view-full-height');
        }

        // Create header, body, and footer sections
        this.header = document.createElement("div");
        this.header.classList.add(...this.config.headerClass.split(' '));

        this.body = document.createElement("div");
        this.body.classList.add('data-view-body');
        if (this.config?.bodyClass?.length > 0) {
            this.body.classList.add(...this.config.bodyClass.split(' '));
        }


        this.footer = document.createElement("div");
        this.footer.classList.add(...this.config.footerClass.split(' '));

        this.dataViewContainer.appendChild(this.header);
        this.dataViewContainer.appendChild(this.body);

        this.setupEventHandlers();
        this.applyStyles();

        // Initialize filter form before loading data
        if (this.config.filter?.enabled) {
            await this.initializeFilterForm();
        }

        // Load initial data after filter initialization
        if (this.config.autoLoad) {
            await this.loadInitialData();
        }
    }


    async initializeFilterForm() {
        this.filterForm = new DataForm({
            eventGroup: `${this.eventGroup}-filter`,
            appClient: this.appClient,
            dataProvider: this.dataProvider,
            mode: 'filter',
            layout: 'dialog', // This configuration is key
            schema: this.config.filter.schema,
            fields: this.config.filter.fields,
            sections: this.config.filter.sections,
            actions: {
                save: true,
                cancel: true
            }
        });

        await this.filterForm.initialize();

        // Listen to save action for applying filter
        this.filterForm.on(`dataSaved`, (filterData) => {
            this.handleFilterChange(filterData);
            this.hideFilterDialog(); // Hide after saving
        });

        // Listen to cancel action
        this.filterForm.on(`cancel`, () => {
            this.hideFilterDialog(); // Hide on cancel
        });
    }




    async handleFilterChange(filterData) {
        // Process filter data to remove empty values
        const processedFilter = {};
        if (filterData) {
            Object.entries(filterData).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    if (Array.isArray(value) && value.length > 0) {
                        processedFilter[key] = value;
                    } else if (!Array.isArray(value)) {
                        processedFilter[key] = value;
                    }
                }
            });
        }

        this.header.innerHTML = '';
        this.filterData = processedFilter;
        this.currentPage = 1;  // Reset to first page when filter changes
        await this.loadData();
    }


    async loadData() {
        try {
            this.loading = true;
            this.render();

            const params = {
                page: this.currentPage,
                pageSize: this.config.pageSize,
                filter: this.filterData || {},  // Ensure filter is an object
                sort: {}  // Add default sort if needed
            };

            await this.dataProvider.loadData(params);
        } catch (error) {
            this.error = error;
            this.log.error(`[${this.eventGroup}] Error loading data:`, error);
            this.render();
        }
    }





    async loadInitialData() {
        try {
            this.log.info(`[${this.eventGroup}] Starting initial data load`);
            this.loading = true;
            this.loadingPage = 1; // Set loading page for initial load
            this.currentPage = 1;
            this.data = []; // Reset data
            this.totalCount = 0; // Reset total count
            this.render(); // Show loading state

            await this.dataProvider.loadData({
                page: 1,
                pageSize: this.config.pageSize,
                filter: this.filterData || {} // Include current filter
            });
        } catch (error) {
            this.error = error;
            this.loadingPage = null; // Reset loading page on error
            this.log.error(`[${this.eventGroup}] Error loading initial data:`, error);
            this.render();
        }
    }






    async loadMoreData() {
        if (this.loading || this.data.length >= this.totalCount) return;

        try {
            this.loading = true;
            this.loadingPage = this.currentPage + 1; // Track which page we're loading

            this.log.info(`[${this.eventGroup}] Loading page ${this.loadingPage}`);

            await this.dataProvider.loadData({
                page: this.loadingPage,
                pageSize: this.config.pageSize,
                filter: this.filterData || {} // Include current filter
            });

        } catch (error) {
            this.error = error;
            this.loadingPage = null; // Reset loading page on error
            this.log.error(`[${this.eventGroup}] Error loading more data:`, error);
        }
    }





    handleSingleSelect(item) {
        this.selectedItems = [item];
        this.highlightSelectedItems();
        this.emit(`itemSelected`, item);
    }

    // And handleMultiSelect
    handleMultiSelect(item, ctrlKey) {
        if (!this.selectedItems) {
            this.selectedItems = [];
        }

        if (ctrlKey) {
            const index = this.selectedItems.findIndex(i => i[this.config.idField] === item[this.config.idField]);
            if (index === -1) {
                this.selectedItems.push(item);
            } else {
                this.selectedItems.splice(index, 1);
            }
        } else {
            this.selectedItems = [item];
        }

        this.highlightSelectedItems();
        this.emit(`selectionChanged`, this.selectedItems);
    }


    highlightSelectedItems() {
        // Remove all existing selections
        this.body.querySelectorAll('.selected').forEach(el => {
            el.classList.remove('selected');
        });

        // Add selection to current items
        this.selectedItems?.forEach(item => {
            const element = this.body.querySelector(`[data-id="${item[this.config.idField]}"]`);
            if (element) {
                if (this.config.viewMode === 'card') {
                    // For card view, add selected class to the card element
                    element.classList.add('selected');
                } else {
                    // For other views, add selected class to the row/item
                    element.classList.add('selected');
                }
            }
        });
    }



    setupEventHandlers() {
        // Infinite scroll handler
        this.body.addEventListener('scroll', this.handleScroll.bind(this));

        // DataProvider events
        if (this.dataProvider) {
            this.dataProvider.on(`dataLoaded`,
                this.handleDataLoaded.bind(this));
            this.dataProvider.on(`dataChanged`,
                this.handleDataChanged.bind(this));
            this.dataProvider.on(`dataError`,
                this.handleDataError.bind(this));
        }

        // Retry button handler
        this.body.addEventListener('click', (e) => {
            if (e.target.closest('[data-action="retry"]')) {
                this.loadInitialData();
            }
        });

        this.header.addEventListener('click', (e) => {
            const button = e.target.closest('[data-action]');
            if (!button) return;

            const action = button.dataset.action;
            switch (action) {
                case 'create':
                    this.emit(`createItem`);
                    break;
                case 'refresh':
                    this.refreshData();
                    break;
                case 'show-filter': // New action for the filter button
                    this.showFilterDialog();
                    break;
                case 'clear-filter': // New action for the clear filter button
                    this.clearFilter();
                    break;
            }
        });


    }

    async refreshData() {
        this.header.innerHTML = '';
        this.currentPage = 1; // Reset to first page
        this.data = []; // Clear existing data
        await this.loadInitialData();
        this.emit(`refreshed`);
    }


    getCurrentFilter() {
        return this.filterData || {};
    }
    getCurrentSort() {
        return {}; // Implement sorting logic if needed
    }

    setFilter(filterData) {
        this.filterData = filterData;
    }



    handleScroll(event) {
        if (this.loading || this.error) return;

        const { scrollTop, scrollHeight, clientHeight } = event.target;
        const remainingScroll = scrollHeight - scrollTop - clientHeight;

        // this.log.debug(`[${this.eventGroup}] Scroll position:`, {
        //     scrollTop,
        //     scrollHeight,
        //     clientHeight,
        //     remainingScroll,
        //     dataLength: this.data.length,
        //     totalCount: this.totalCount
        // });

        if (remainingScroll <= 100) { // 100px threshold
            this.loadMoreData();
        }
    }


    handleDataLoaded({ data, totalCount }) {
        this.log.info(`[${this.eventGroup}] Data loaded:`, {
            dataLength: data?.length,
            totalCount,
            currentPage: this.currentPage,
            loadingPage: this.loadingPage
        });

        this.error = null;
        this.loading = false;

        // If this is initial load or reload
        if (!this.loadingPage || this.loadingPage === 1) {
            this.data = [...data];
            this.currentPage = 1;
        } else {
            // Append new data to existing data
            this.data = [...this.data, ...data];
            this.currentPage = this.loadingPage; // Update current page to the loaded page
        }

        this.loadingPage = null; // Reset loading page
        this.totalCount = totalCount;
        this.render();

        this.log.info(`[${this.eventGroup}] After data loaded:`, {
            dataLength: this.data.length,
            totalCount: this.totalCount,
            currentPage: this.currentPage
        });
    }


    handleDataChanged({ data, totalCount }) {
        this.log.info(`[${this.eventGroup}] Data changed:`, {
            dataLength: data?.length,
            totalCount
        });
        this.data = [...data];
        this.totalCount = totalCount;
        this.render();
    }

    handleDataError(error) {
        this.error = error;
        this.render();
    }

    gridTemplate(data) {
        return this.html`
            <div class="table-responsive">
                <table class="table table-hover mb-0">
                    <thead>
                        <tr>
                            ${this.config.fields.map(field => this.html`
                                <th scope="col">${this.t(field.label || field.name)}</th>
                            `)}
                            ${this.config.actions.length ? this.html`<th scope="col" style="width: 100px;">Actions</th>` : ''}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(item => this.html`
                            <tr data-id="${item[this.config.idField]}" class="cursor-pointer">
                                ${this.config.fields.map(field => this.html`
                                    <td>${this.renderField(item, field)}</td>
                                `)}
                                ${this.config.actions.length ? this.html`
                                    <td>
                                        <div class="btn-group btn-group-sm gap-1" style="opacity: 1;">
                                            ${this.renderActions(item)}
                                        </div>
                                    </td>
                                ` : ''}
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `.toString();
    }

    listTemplate(data) {
        return this.html`
            <div class="list-group list-group-flush">
                ${data.map(item => this.html`
                    <div class="list-group-item cursor-pointer" data-id="${item[this.config.idField]}">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="flex-grow-1">
                                ${this.config.fields.map(field => this.html`
                                    <div class="mb-1">
                                        <small class="text-muted">${this.t(field.label || field.name)}</small>:
                                        <span>${this.renderField(item, field)}</span>
                                    </div>
                                `)}
                            </div>
                            ${this.config.actions.length ? this.html`
                                <div class="btn-group btn-group-sm gap-1">
                                    ${this.renderActions(item)}
                                </div>
                            ` : ''}
                        </div>
                    </div>
                `)}
            </div>
        `.toString();
    }

    cardTemplate(data) {
        return this.html`
            <div class="row w-100 g-2">
                ${data.map(item => this.html`
                    <div class="col-12 col-md-6 col-lg-4">
                        <div class="card" data-id="${item[this.config.idField]}">
                            <div class="card-body cursor-pointer">
                                ${this.config.fields.map(field => this.html`
                                    <div class="mb-2">
                                        <small class="text-muted">${this.t(field.label || field.name)}</small>
                                        <div>${this.renderField(item, field)}</div>
                                    </div>
                                `)}
                            </div>
                            ${this.config.actions.length ? this.html`
                                <div class="card-footer bg-transparent">
                                    <div class="btn-group btn-group-sm gap-1">
                                        ${this.renderActions(item)}
                                    </div>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                `)}
            </div>
        `.toString();
    }

    defaultItemTemplate(item) {
        return this.html`
            <div class="data-view-item" data-id="${item[this.config.idField]}">
                ${this.config.fields.map(field => this.html`
                    <div class="data-view-item-field">
                        <span class="field-label">${this.t(field.label || field.name)}:</span>
                        <span class="field-value">${this.renderField(item, field)}</span>
                    </div>
                `)}
            </div>
        `.toString();
    }

    actionButtonTemplate(action, item) {
        // Note: data attributes are safe to inject raw if they don't contain quotes, but html`` handles it.
        return this.html`
            <button type="button"
                    class="btn btn-icon-chip ${action.className || ''}"
                    data-action="${action.eventName}"
                    data-id="${item[this.config.idField]}"
                    title="${this.t(action.label)}">
                ${action.icon ? this.html`<i class="${action.icon}"></i>` : this.t(action.label)}
            </button>
        `.toString();
    }

    emptyStateTemplate() {
        const config = this.config.emptyStateConfig;
        return this.html`
            <div class="ab-empty">
                <i class="${config.icon} ab-empty-icon"></i>
                <div class="ab-empty-title" data-i18n="${config.message}">${this.t(config.message)}</div>
                <div class="ab-empty-body" data-i18n="${config.description}">${this.t(config.description)}</div>
                ${config.actionButton ? this.html`
                    <button class="btn btn-primary mt-3" data-i18n="${config.actionButton.label}" data-action="${config.actionButton.event}">
                        ${this.t(config.actionButton.label)}
                    </button>
                ` : ''}
            </div>
        `.toString();
    }

    loadingTemplate() {
        return this.html`
            <div class="skeleton-loader">
                <div class="skeleton-row">
                    <div class="skeleton-block" style="width: 25%;"></div>
                    <div class="skeleton-block" style="width: 40%;"></div>
                    <div class="skeleton-block" style="width: 20%;"></div>
                    <div class="skeleton-block" style="width: 15%;"></div>
                </div>
                <div class="skeleton-row">
                    <div class="skeleton-block" style="width: 30%;"></div>
                    <div class="skeleton-block" style="width: 35%;"></div>
                    <div class="skeleton-block" style="width: 15%;"></div>
                    <div class="skeleton-block" style="width: 20%;"></div>
                </div>
                <div class="skeleton-row">
                    <div class="skeleton-block" style="width: 20%;"></div>
                    <div class="skeleton-block" style="width: 45%;"></div>
                    <div class="skeleton-block" style="width: 25%;"></div>
                    <div class="skeleton-block" style="width: 10%;"></div>
                </div>
                <div class="skeleton-row">
                    <div class="skeleton-block" style="width: 35%;"></div>
                    <div class="skeleton-block" style="width: 25%;"></div>
                    <div class="skeleton-block" style="width: 30%;"></div>
                    <div class="skeleton-block" style="width: 10%;"></div>
                </div>
            </div>
        `.toString();
    }

    errorTemplate(error) {
        return this.html`
            <div class="text-center p-5">
                <i class="bi bi-exclamation-circle display-1 text-danger mb-3"></i>
                <h4 class="text-danger">Error Loading Data</h4>
                <p class="text-muted mb-3">${error.message}</p>
                <button class="btn btn-primary" data-action="retry">Retry</button>
            </div>
        `.toString();
    }

    renderField(item, field) {
        const value = item[field.name];
        if (field.reference) {
            const displayValue = item[`${field.name}Display`] || value;
            if (field.render) {
                // Custom renderers must return SafeHtml or string. We trust them.
                const rendered = field.render(value, { ...item, [`${field.name}Display`]: displayValue });
                return rendered instanceof this.appClient.SafeHtml ? rendered : this.trust(rendered);
            }
            return displayValue || ''; // html`` will escape this
        }
        if (field.render) {
            const rendered = field.render(value, item);
            // If the user returns a string from render(), we assume they might want HTML (e.g. badges).
            // Ideally, they should use this.html`` too, but for backward compatibility with existing renderers
            // that return strings like '<span class="badge">...</span>', we might need to trust it.
            // HOWEVER, to be secure, we should require them to use SafeHtml.
            // For now, let's assume render() returns trusted HTML string.
            return this.trust(rendered);
        }
        return value ?? ''; // html`` will escape this
    }

    renderActions(item) {
        return this.config.actions
            .filter(action => typeof action.visibleWhen !== 'function' || action.visibleWhen(item))
            .map(action => this.html`
                <button type="button"
                        class="btn btn-icon-chip ${action.className || ''}"
                        data-action="${action.eventName}"
                        data-id="${item[this.config.idField]}"
                        title="${this.t(action.label)}">
                    ${action.icon ? this.html`<i class="${action.icon}"></i>` : ''}
                    ${action.label && !action.icon ? this.t(action.label) : ''}
                </button>
            `);
    }

    async showFilterDialog() {
        // --- MODIFICATION: Use the new show() method ---
        this.filterForm.setData(this.filterData || {});
        this.filterForm.show(); // Simply call show()
    }

    hideFilterDialog() {
        // --- MODIFICATION: Use the new hide() method ---
        this.filterForm.hide(); // Simply call hide()
    }


    clearFilter() {
        this.header.innerHTML = '';
        this.filterData = null;
        this.currentPage = 1;
        this.loadData();
    }




    async render() {
        this.log.info(`[${this.eventGroup}] Rendering:`, {
            dataLength: this.data?.length,
            totalCount: this.totalCount,
            loading: this.loading,
            error: this.error,
            currentPage: this.currentPage,
            loadingPage: this.loadingPage
        });

        this.cleanupActionHandlers();

        // Build header only if it is empty
        if (!this.header.innerHTML || this.header.childNodes.length === 0) {
            if (this.config.toolbar?.enabled) {
                // Render header with unified toolbar
                const headerContent = document.createElement("div");
                headerContent.classList.add("data-view-toolbar", "w-100");

                // Left side: All control buttons in a single button group
                const leftControls = document.createElement("div");
                leftControls.classList.add("d-flex", "align-items-center", "gap-3", "flex-grow-1");

                const buttonGroup = document.createElement("div");
                buttonGroup.classList.add("btn-group", "btn-group-sm", "gap-2");

                if (this.config.toolbar?.enabled && this.config.toolbar.viewModes?.enabled) {
                    const alignBtnGroup = document.createElement("div");
                    alignBtnGroup.classList.add("btn-group", "btn-group-sm");
                    // Add view mode buttons
                    const viewModes = [
                        { mode: 'grid', icon: 'bi-grid', label: 'Grid' },
                        { mode: 'list', icon: 'bi-list', label: 'List' },
                        { mode: 'card', icon: 'bi-grid-3x3-gap', label: 'Card' }
                    ];

                    viewModes.forEach(({ mode, icon, label }) => {
                        const button = document.createElement("button");
                        button.type = "button";
                        button.classList.add("btn", "btn-outline-secondary");
                        if (mode === this.getActiveViewMode()) {
                            button.classList.add("active");
                        }
                        button.setAttribute("data-view-mode", mode);
                        button.innerHTML = `<i class="bi ${icon}"></i>`;
                        button.title = label;
                        alignBtnGroup.appendChild(button);
                    });

                    buttonGroup.appendChild(alignBtnGroup);
                }
                // Add Create button if enabled
                if (this.config.toolbar?.enabled && this.config.toolbar.create?.enabled) {
                    const createButton = document.createElement("button");
                    createButton.type = "button";
                    createButton.classList.add("btn", "btn-primary", "d-flex", "gap-1");
                    createButton.setAttribute("data-action", "create");

                    const createIcon = document.createElement("i");
                    createIcon.classList.add(...(this.config.toolbar.create.icon || 'bi bi-plus-lg').split(" "));
                    createButton.appendChild(createIcon);

                    const createLabel = document.createElement("span");
                    createLabel.setAttribute("data-i18n", "Create");
                    createLabel.textContent = this.t(this.config.toolbar.create.label || 'New');
                    createButton.appendChild(createLabel);

                    buttonGroup.appendChild(createButton);
                }

                // Add Refresh button if enabled
                if (this.config.toolbar?.enabled && this.config.toolbar.refresh?.enabled) {
                    const refreshButton = document.createElement("button");
                    refreshButton.type = "button";
                    refreshButton.classList.add("btn", "btn-outline-secondary", "d-flex", "gap-1");
                    refreshButton.setAttribute("data-action", "refresh");

                    const refreshIcon = document.createElement("i");
                    refreshIcon.classList.add(...(this.config.toolbar.refresh.icon || 'bi bi-arrow-clockwise').split(" "));
                    refreshButton.appendChild(refreshIcon);

                    const refreshLabel = document.createElement("span");
                    refreshLabel.setAttribute("data-i18n", "Refresh");
                    refreshLabel.textContent = this.t(this.config.toolbar.refresh.label || 'Refresh');
                    refreshButton.appendChild(refreshLabel);

                    buttonGroup.appendChild(refreshButton);
                }

                leftControls.appendChild(buttonGroup);

                // Add Search Box
                if (this.config.search?.enabled !== false) {
                    const searchContainer = document.createElement("div");
                    searchContainer.classList.add("toolbar-search");
                    searchContainer.innerHTML = `
                        <i class="bi bi-search"></i>
                        <input type="search" class="form-control form-control-sm" placeholder="${this.t('Search...')}" value="${this.searchQuery || ''}">
                    `;
                    const searchInput = searchContainer.querySelector("input");
                    searchInput.addEventListener("input", (e) => {
                        this.searchQuery = e.target.value;
                        this.renderBody();
                    });
                    leftControls.appendChild(searchContainer);
                }

                const rightControls = document.createElement("div");
                if (this.config.filter?.enabled) {
                    const filterControls = document.createElement("div");
                    filterControls.classList.add("d-flex", "gap-2");

                    // Filter button
                    const filterButton = document.createElement("button");
                    filterButton.classList.add("btn", "btn-outline-secondary", "btn-sm", "d-flex", "gap-1");
                    filterButton.setAttribute("data-action", "show-filter");

                    const filterIcon = document.createElement("i");
                    filterIcon.classList.add("bi", `bi-funnel${this.filterData ? '-fill' : ''}`);
                    filterButton.appendChild(filterIcon);

                    const filterLabel = document.createElement("span");
                    filterLabel.setAttribute("data-i18n", "Filters");
                    filterLabel.textContent = this.t("Filters");
                    filterButton.appendChild(filterLabel);

                    if (this.filterData) {
                        filterButton.classList.add("active");
                    }
                    filterControls.appendChild(filterButton);

                    // Clear filter button
                    if (this.filterData) {
                        const clearFilterButton = document.createElement("button");
                        clearFilterButton.classList.add("btn", "btn-outline-danger", "btn-sm", "d-flex", "gap-1");
                        clearFilterButton.setAttribute("data-action", "clear-filter");

                        const clearFilterIcon = document.createElement("i");
                        clearFilterIcon.classList.add("bi", "bi-x-circle");
                        clearFilterButton.appendChild(clearFilterIcon);

                        const clearFilterLabel = document.createElement("span");
                        clearFilterLabel.textContent = "Clear";
                        clearFilterButton.appendChild(clearFilterLabel);

                        filterControls.appendChild(clearFilterButton);
                    }

                    rightControls.appendChild(filterControls);
                }

                headerContent.appendChild(leftControls);
                headerContent.appendChild(rightControls);
                this.header.appendChild(headerContent);

                // Add event listener for view mode buttons
                buttonGroup.addEventListener('click', (e) => {
                    const viewModeButton = e.target.closest('[data-view-mode]');
                    if (viewModeButton) {
                        const mode = viewModeButton.dataset.viewMode;
                        this.setViewMode(mode);
                    }
                });
            }
        } else {
            // Update active state of view mode buttons if header already exists
            const viewModeButtons = this.header.querySelectorAll('[data-view-mode]');
            viewModeButtons.forEach(btn => {
                if (btn.dataset.viewMode === this.getActiveViewMode()) {
                    btn.classList.add('active');
                } else {
                    btn.classList.remove('active');
                }
            });
        }

        // Render body
        this.renderBody();

        this.appClient.tx.applyTranslations(this.dataViewContainer);

        return this.dataViewContainer;
    }

    renderBody() {
        this.cleanupActionHandlers();
        this.body.innerHTML = '';

        let renderedData = this.data;
        if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            renderedData = this.data.filter(item => {
                return this.config.fields.some(field => {
                    const val = item[field.name];
                    if (val === null || val === undefined) return false;
                    return String(val).toLowerCase().includes(query);
                });
            });
        }

        if (this.loading && (!renderedData || renderedData.length === 0)) {
            this.body.innerHTML = this.config.templates.loadingTemplate();
        } else if (this.error && (!renderedData || renderedData.length === 0)) {
            this.body.innerHTML = this.config.templates.errorTemplate(this.error);
        } else if (!renderedData || renderedData.length === 0) {
            this.body.innerHTML = this.config.templates.emptyStateTemplate();
        } else {
            const viewModeTemplate = this.config.templates[`${this.getActiveViewMode()}Template`];
            if (viewModeTemplate) {
                this.body.innerHTML = viewModeTemplate(renderedData);
            }

            // Add loading indicator at the bottom if loading more
            if (this.loading) {
                const loadingIndicator = document.createElement('div');
                loadingIndicator.innerHTML = this.config.templates.loadingTemplate();
                this.body.appendChild(loadingIndicator);
            }
        }

        this.setupActionHandlers();
        this.highlightSelectedItems();
        this.appClient.tx.applyTranslations(this.body);
    }

    setupActionHandlers() {
        // First, ensure any old listeners are gone.
        this.cleanupActionHandlers();

        // Create a single, bound handler function for the lifetime of this listener attachment.
        this.boundActionHandler = (e) => {
            const actionButton = e.target.closest('[data-action]');
            if (actionButton) {
                e.preventDefault();
                e.stopPropagation();

                const action = actionButton.dataset.action;
                const itemId = actionButton.closest('[data-id]')?.dataset.id;
                const item = this.data.find(i => i[this.config.idField] === itemId);

                if (item) {
                    this.log.info(`[${this.eventGroup}] Action clicked:`, { action, itemId });
                    this.emit(`${action}`, item);
                }
            }
        };

        // Attach the new, single listener.
        this.body.addEventListener('click', this.boundActionHandler);

        // Item selection handlers (this logic is generally safe as it re-queries the DOM, but we can make it more robust too)
        const items = this.body.querySelectorAll('[data-id]');
        items.forEach(item => {
            const clickTarget = item.querySelector('.card-body') || item; // Handle card view or other views

            // To be extra safe, we can manage these listeners too, though it's less likely to be the source of the problem.
            // For now, let's focus on the main action handler which is the likely culprit.
            clickTarget.addEventListener('click', (e) => {
                if (e.target.closest('[data-action]')) return;
                const itemId = item.dataset.id;
                const dataItem = this.data.find(i => i[this.config.idField] === itemId);
                if (dataItem) {
                    if (this.config.multiSelect) {
                        this.handleMultiSelect(dataItem, e.ctrlKey);
                    } else {
                        this.handleSingleSelect(dataItem);
                    }
                }
            });
        });
    }



    cleanupActionHandlers() {
        if (this.boundActionHandler && this.body) {
            this.body.removeEventListener('click', this.boundActionHandler);
            this.boundActionHandler = null;
        }
    }



    getActiveViewMode() {
        const mode = this.config.viewMode || 'grid';
        if (mode === 'grid' && window.innerWidth < 768) {
            if (this.config.templates?.cardTemplate) {
                return 'card';
            } else if (this.config.templates?.listTemplate) {
                return 'list';
            }
        }
        return mode;
    }

    setViewMode(viewMode) {
        this.config.viewMode = viewMode;
        this.render();
    }

    destroy() {
        // Ensure all listeners are cleaned up when the component is fully destroyed.
        this.cleanupActionHandlers();
        this.body?.removeEventListener('scroll', this.handleScroll.bind(this));
        if (this.dataProvider) {
            this.dataProvider.off(`${this.dataProvider.eventGroup}-dataLoaded`);
            this.dataProvider.off(`${this.dataProvider.eventGroup}-dataChanged`);
            this.dataProvider.off(`${this.dataProvider.eventGroup}-dataError`);
        }
        this.data = [];
        this.totalCount = 0;
        this.currentPage = 1;
        this.loading = false;
        this.error = null;
        super.destroy();
    }

}
